import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Layout } from './components/Layout';
import { Dashboard } from './pages/Dashboard';
import { CancellationDetail } from './pages/CancellationDetail';
import { PatientManagement } from './pages/PatientManagement';
import { PhysioAdmin } from './pages/PhysioAdmin';
import { Map } from './pages/Map';
import { Polls } from './pages/Polls';
import { RespondToPoll } from './pages/RespondToPoll';
import { StatusBoard } from './pages/StatusBoard';
import { PhysioStatus } from './pages/PhysioStatus';
import { PhysioLookup } from './pages/PhysioLookup';
import { PublicTeamStatus } from './pages/PublicTeamStatus';
import { Settings } from './pages/Settings';
import { Login } from './pages/Login';
import { Account } from './pages/Account';
import { AdminUsers } from './pages/AdminUsers';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

/** Wraps licensed-only routes — unlicensed users get sent to /account */
function LicensedRoute({ children }: { children: React.ReactNode }) {
  const { isLicensed } = useAuth();

  if (!isLicensed) {
    return <Navigate to="/account" replace />;
  }

  return <>{children}</>;
}

/** Admin-only route guard */
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAdmin } = useAuth();

  if (!isAdmin) {
    return <Navigate to="/account" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/respond/:pollToken" element={<RespondToPoll />} />
      <Route path="/respond/:pollToken/:responseToken" element={<RespondToPoll />} />
      <Route path="/status/team" element={<PublicTeamStatus />} />
      <Route path="/status" element={<PhysioLookup />} />
      <Route path="/status/:statusToken" element={<PhysioStatus />} />
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <Layout>
              <Routes>
                {/* Account — always accessible to logged-in users */}
                <Route path="/account" element={<Account />} />

                {/* Admin routes */}
                <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />

                {/* Licensed routes — redirect unlicensed users to /account */}
                <Route path="/" element={<LicensedRoute><Dashboard /></LicensedRoute>} />
                <Route path="/cancellations/:id" element={<LicensedRoute><CancellationDetail /></LicensedRoute>} />
                <Route path="/patients" element={<LicensedRoute><PatientManagement /></LicensedRoute>} />
                <Route path="/physios" element={<LicensedRoute><PhysioAdmin /></LicensedRoute>} />
                <Route path="/team-status" element={<LicensedRoute><StatusBoard /></LicensedRoute>} />
                <Route path="/polls" element={<LicensedRoute><Polls /></LicensedRoute>} />
                <Route path="/map" element={<LicensedRoute><Map /></LicensedRoute>} />
                <Route path="/settings" element={<LicensedRoute><Settings /></LicensedRoute>} />
              </Routes>
            </Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
